import { AbstractClient, CompositionClient } from "@/app/client";
import * as types from "@/app/api/types/monitoring";

/**
 * Client for the scoped monitoring dashboards (STK-1191). Scope is resolved
 * server-side from the caller's identity — `group_ids` / `user_ids` may only
 * narrow it.
 */
export default class APIMonitoring extends CompositionClient {
  constructor(client: AbstractClient) {
    super(client);
  }

  private async _getJson<T>(url: string): Promise<T> {
    const res = await this._fetch({
      url,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json"))
      return res.json();
    throw new Error("Bad content type");
  }

  /** Append the range + narrowing filters shared by overview and breakdown. */
  private _appendCommon(
    search: URLSearchParams,
    params: types.MonitoringQuery,
  ): void {
    if (params.start_date) search.append("start_date", params.start_date);
    if (params.end_date) search.append("end_date", params.end_date);
    for (const id of params.group_ids ?? []) search.append("group_ids", id);
    for (const id of params.user_ids ?? []) search.append("user_ids", id);
    if (params.category != null)
      search.append("category", String(params.category));
  }

  /** The caller's monitoring level and the groups/users they may filter by. */
  getScope = (): Promise<types.MonitoringScope> =>
    this._getJson<types.MonitoringScope>("/monitoring/scope");

  /** Headline metric cards (orders, lines, AI precision, volume). */
  getOverview = (
    params: types.OverviewQuery = {},
  ): Promise<types.MonitoringOverview> => {
    const search = new URLSearchParams();
    this._appendCommon(search, params);
    if (params.granularity) search.append("granularity", params.granularity);
    if (params.transport_mode) search.append("transport_mode", "true");
    const qs = search.toString();
    const suffix = qs ? `?${qs}` : "";
    return this._getJson<types.MonitoringOverview>(
      `/monitoring/overview${suffix}`,
    );
  };

  /** Ranked breakdown of a dimension by a metric. */
  getBreakdown = (
    params: types.BreakdownQuery = {},
  ): Promise<types.MonitoringBreakdown> => {
    const search = new URLSearchParams();
    if (params.dimension) search.append("dimension", params.dimension);
    if (params.metric) search.append("metric", params.metric);
    if (params.sort) search.append("sort", params.sort);
    this._appendCommon(search, params);
    if (params.limit != null) search.append("limit", String(params.limit));
    if (params.transport_mode) search.append("transport_mode", "true");
    const qs = search.toString();
    const suffix = qs ? `?${qs}` : "";
    return this._getJson<types.MonitoringBreakdown>(
      `/monitoring/breakdown${suffix}`,
    );
  };
}
